import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Card,
  Link,
  Typography,
  makeStyles
} from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { fetchEncryptedContent, getCompany } from '../../services/ApiService'
import moment from 'moment'
import { generateTermsPDF, getSignedS3Url } from '../../lib/Global'
import { useDispatch, useSelector } from 'react-redux'
import { loadingActions } from '../../store/loading'
import md5 from 'md5'
import { sseClientKeys } from '../../lib/Constants'

const useStyles = makeStyles(theme => ({
  card: {
    borderRadius: '8px',
    boxShadow: '6px 9px 43px ' + theme.colors.profile.shadowCard,
    marginBottom: '2em',
    padding: '1em',
    color: theme.colors.text
  },
  cardTitle: {
    fontSize: '20px',
    fontWeight: '700'
  },
  fileCard: {
    backgroundColor: theme.colors.grey_2,
    margin: '1em 0em',
    padding: '0.5em',
    border: '1px solid ' + theme.colors.profile.darkCard,
    borderRadius: '12px',
    boxShadow: 'none',
    width: '200px',
    [theme.breakpoints.down('xs')]: {
      margin: '0',
      width: '100%'
    }
  },
  fileTitle: {
    fontSize: '12px',
    marginBottom: '0.5em'
  },
  fileData: {
    fontSize: '12px',
    paddingBottom: '0.5em'
  },
  fileMissingData: {
    fontStyle: 'italic',
    color: theme.colors.grey
  },
  fileLink: {
    fontSize: '12px',
    textDecoration: 'none',
    color: theme.colors.iconBlue,
    paddingBottom: '0.5em',
    wordBreak: 'break-all'
  },
  editButton: {
    alignSelf: 'flex-start',
    marginLeft: 'auto',
    marginRight: '0',
    color: theme.colors.iconBlue,
    textTransform: 'none',
    fontSize: '16px',
    fontWeight: '600',
    letterSpacing: '0.05em',
    lineHeight: '19px'
  },
  cardError: {
    color: theme.colors.errorColor,
    fontSize: '12px',
    marginBottom: '0.5em'
  }
}))

export const TermsAndConditionsCard = props => {
  const classes = useStyles()
  const { data, originatingCompany, content } = props
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const userStore = useSelector(state => state.auth.user)
  const [processedContent, setProcessedContent] = useState('')

  const termsSignature =
    userStore.userInfo?.configurations?.portals?.subcontractor?.onboarding
      ?.compliance?.terms?.mandatory_signature

  useEffect(() => {
    setProcessedContent(content)

    // Only process if we have content, data with signature URL
    if (termsSignature && content && data && data.signature_url) {
      processContentWithSignature()
    }
  }, [content, data])

  const processContentWithSignature = async () => {
    if (!termsSignature || !content || !data || !data.signature_url) return

    dispatch(loadingActions.show())

    try {
      const clientId = userStore?.userInfo?.parent_subdomain
      const signedUrl = await getSignedS3Url(
        data.signature_url,
        '',
        'getObject'
      )

      const sseKey = Buffer.alloc(32, sseClientKeys[clientId])
      const encodedSseKey = encodeURI(sseKey.toString('base64'))
      const md5Hash = Buffer.from(encodeURI(md5(sseKey)), 'hex').toString(
        'base64'
      )

      const response = await fetchEncryptedContent(
        signedUrl,
        encodedSseKey,
        md5Hash
      )

      if (!response.ok) {
        throw new Error(`Failed to fetch signature: ${response.status}`)
      }

      const blob = await response.blob()

      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader()

        reader.onloadend = () => {
          resolve(reader.result)
        }

        reader.onerror = error => {
          reject(error)
        }

        reader.readAsDataURL(blob)
      })

      // Process the HTML content to include signature data
      const parser = new DOMParser()
      const doc = parser.parseFromString(content, 'text/html')

      // Update name fields
      doc
        .querySelectorAll('[data-signature-field="trade-partner-name"]')
        .forEach(el => {
          el.textContent = data.signer_name || ''
        })

      // Update signature fields
      const signatureImageStyle = 'max-width:260px;max-height:65px;'
      doc
        .querySelectorAll('[data-signature-field="trade-partner-signature"]')
        .forEach(el => {
          el.innerHTML = `<img src="${dataUrl}" alt="Signature" class="signature-image" style="${signatureImageStyle}"/>`
        })

      // Update title fields
      doc
        .querySelectorAll('[data-signature-field="trade-partner-title"]')
        .forEach(el => {
          el.textContent = `Title ${data.signer_title || ''}`
        })

      // Update date fields
      doc
        .querySelectorAll('[data-signature-field="trade-partner-date"]')
        .forEach(el => {
          el.textContent = `Date ${moment(new Date(data.signed_on)).format(
            'MM/DD/YYYY'
          )}`
        })

      setProcessedContent(doc.body.innerHTML)
    } catch (error) {
      /* eslint-disable no-console */
      console.error('Error processing content with signature:', error)
      setProcessedContent(content)
    } finally {
      dispatch(loadingActions.hide())
    }
  }

  const AgreementCard = props => {
    const classes = useStyles()
    const { t } = useTranslation()
    const signDate = data?.signed
      ? moment(new Date(data?.signed_on)).format(t('general.format.short_date'))
      : null

    return (
      <Card className={classes.fileCard}>
        <Box>
          <Link onClick={generatePDF} target="_blank" rel="noopener">
            <Typography className={classes.fileLink}>
              {t('company_profile.terms')}
            </Typography>
          </Link>
          <Typography
            className={signDate ? classes.fileTitle : classes.cardError}
          >
            {signDate
              ? t(
                  'company_settings.terms_card.' +
                    (data?.user_id ? 'accepted' : 'accepted_outside')
                ) +
                ', ' +
                signDate
              : t('company_settings.terms_card.not_accepted')}
          </Typography>
        </Box>
      </Card>
    )
  }

  async function generatePDF() {
    dispatch(loadingActions.show())
    const company = await getCompany(originatingCompany)
    const signDate = moment(new Date(data?.signed_on)).format(
      t('general.format.short_date')
    )
    const signTime = moment(new Date(data?.signed_on)).format(
      t('general.format.time_am_pm')
    )
    generateTermsPDF(
      company.name,
      termsSignature ? processedContent : content,
      data?.signed,
      termsSignature ? data?.signer_name : data?.name,
      signDate,
      signTime,
      t
    )
    dispatch(loadingActions.hide())
  }

  const EditButton = props => {
    const classes = useStyles()
    return (
      <Button className={classes.editButton} onClick={props.onClick}>
        {props.label}
      </Button>
    )
  }

  const handleEditProfile = () => {
    props.setComponent('terms')
    props.setOpen(true)
  }
  return (
    <Card className={classes.card}>
      <Box display="flex" flexDirection="row">
        <Typography classes={{ root: classes.cardTitle }}>
          {t('company_settings.card.terms')}
        </Typography>
        <EditButton
          label={t('company_settings.buttons.edit')}
          onClick={handleEditProfile}
        />
      </Box>
      <Box
        display="flex"
        flexDirection="row"
        flexWrap="wrap"
        className={classes.sectionDivider}
      ></Box>
      <Box className={classes.marginContainer}>
        <Box className={classes.sectionDivider}></Box>
        <AgreementCard />
      </Box>
    </Card>
  )
}
